import {
  type AcknowledgePublicationInput,
  type BeginPublicationAttemptInput,
  computePublicationMutationId,
  type DocPublicationHeadStorage,
  type DocPublicationStorage,
  docSyncRemoteApplyOrigin,
  type LocalPublication,
  type LocalPublicationReceipt,
  type PendingPublication,
  PUBLICATION_JOURNAL_FORMAT,
  publicationAttemptId,
  type PublicationCommitMeasurement,
  PublicationError,
  publicationGroupMembers,
  type PublicationMemberBase,
  type PublicationMemberHead,
  publicationRefusalFromReceipt,
  publicationStateFromReceipt,
  type RefusedPublicationResolution,
  type RefusedPublicationStorage,
  type RemotePublicationLocalReceipt,
  validatePublicationCompanions,
  validatePublicationUpdates,
} from '@streamlain/doc-publication-contract';
import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';
import type { IDBPTransaction, StoreNames } from 'idb';

import { share } from '../../connection';
import {
  assertPublicationJournalFormat,
  decodeLocalDoc,
  decodeLocalJson,
  docKeyScope,
  encodeLocalDoc,
  encodeLocalJson,
  journalCompanions,
  localPayloadScope,
} from '../../local-data/payload';
import {
  type DocClock,
  type DocClocks,
  type DocRecord,
  DocStorageBase,
  type DocUpdate,
  type RecoveryVersionStore,
} from '../../storage';
import type { RemotePublicationApply } from '../../storage/doc-publication';
import { computeChecksum, verifyChecksumDetailed } from '../../utils/checksum';
import { isQuotaExceededError } from '../../utils/quota';
import { IDBConnection, type IDBConnectionOptions } from './db';
import { IDBEncryptionContext } from './encryption';
import { readRecoveryVersion } from './local-erase';
import { IndexedDBLocker } from './lock';
import { putPeerClocksMax } from './peer-clocks';
import { IndexedDBRefusedPublicationStorage } from './refused-publications';
import { withIdbRetry } from './retry';
import type { DocStorageSchema, IDBPublicationRecord } from './schema';

// Keep replay work bounded for writers that do not run a DocSync reader.
// This is a maintenance trigger, not a publication admission limit.
const PUBLICATION_BASE_COMPACTION_UPDATES = 128;

class SnapshotCASMiss extends Error {
  constructor() {
    super('SNAPSHOT_CAS_MISS');
  }
}

type ChannelMessage =
  | { type: 'update'; update: DocRecord; origin?: string }
  | { type: 'replaced'; docId: string };

// Plain data adds authState through the encryption check (publicationScope).
const publicationStores: StoreNames<DocStorageSchema>[] = [
  'updates',
  'clocks',
  'publications',
  'publicationMeta',
  'publicationActors',
];

type PublicationTransaction<Mode extends IDBTransactionMode = 'readwrite'> =
  IDBPTransaction<DocStorageSchema, StoreNames<DocStorageSchema>[], Mode>;

export class IndexedDBDocStorage
  extends DocStorageBase<IDBConnectionOptions>
  implements
    DocPublicationStorage,
    DocPublicationHeadStorage,
    RefusedPublicationStorage,
    RecoveryVersionStore
{
  static readonly identifier = 'IndexedDBDocStorage';

  readonly connection = share(new IDBConnection(this.options));
  private readonly refusedPublications = new IndexedDBRefusedPublicationStorage(
    this.connection
  );
  listRefusedPublications(docIds: readonly string[]) {
    return this.refusedPublications.listRefusedPublications(docIds);
  }
  resolveRefusedPublications(
    docIds: readonly string[],
    resolution: RefusedPublicationResolution
  ) {
    return this.refusedPublications.resolveRefusedPublications(
      docIds,
      resolution
    );
  }

  private lastUpdateTimestampMs = 0;
  private readonly snapshotTokens = new WeakMap<DocRecord, string>();
  private readonly updateTokens = new WeakMap<DocRecord, string>();
  private get encryption() {
    return this.connection.encryption;
  }
  private async readDocPayload(
    context: IDBEncryptionContext,
    record:
      | DocStorageSchema['updates']['value']
      | DocStorageSchema['snapshots']['value'],
    kind: 'update' | 'snapshot'
  ): Promise<{ bin: Uint8Array; editor?: string; requestId?: string }> {
    if (this.encryption.plain) {
      if (!(record.bin instanceof Uint8Array))
        throw localAccountKeyError('CORRUPT');
      if (record.checksum) {
        const check = await verifyChecksumDetailed(record.bin, record.checksum);
        if (!check.valid)
          this.emit('corruption', {
            type: kind,
            docId: record.docId,
            ...(kind === 'update' && 'createdAt' in record
              ? { timestamp: record.createdAt }
              : {}),
            expected: check.expected,
            actual: check.actual,
          });
      }
      return { bin: record.bin };
    }
    if (!record.storageRevision || record.bin !== undefined)
      throw localAccountKeyError('CORRUPT');
    return decodeLocalDoc(
      await context.open(
        docKeyScope(record.docId),
        localPayloadScope(kind, record.docId),
        kind,
        record.payload
      )
    );
  }
  private async encodeDocPayload(
    context: IDBEncryptionContext,
    update: DocUpdate,
    kind: 'update' | 'snapshot'
  ) {
    if (this.encryption.plain)
      return {
        bin: new Uint8Array(update.bin),
        checksum: await computeChecksum(update.bin),
      };
    const keyScopeId = docKeyScope(update.docId);
    const payload = await context.seal(
      keyScopeId,
      localPayloadScope(kind, update.docId),
      kind,
      encodeLocalDoc(update.bin, {
        editor: update.editor,
        requestId: update.requestId,
      })
    );
    return { payload, keyScopeId, storageRevision: crypto.randomUUID() };
  }

  get db() {
    return this.connection.inner.db;
  }

  get channel() {
    return this.connection.inner.channel;
  }

  override locker = new IndexedDBLocker(this.connection);

  private publicationBinding() {
    const binding = this.options.authBinding;
    if (
      binding === undefined ||
      (binding === null && this.options.flavour !== 'local') ||
      (binding !== null && this.options.flavour === 'local')
    ) {
      throw new PublicationError('LOCKED');
    }
    return binding;
  }

  /** IDB requests are the only awaited work while a publication transaction is live. */
  private async publicationTransaction<
    T,
    Mode extends 'readonly' | 'readwrite',
  >(
    mode: Mode,
    stores: StoreNames<DocStorageSchema>[],
    work: (tx: PublicationTransaction<Mode>) => Promise<T>,
    signal?: AbortSignal
  ): Promise<T> {
    return withIdbRetry(this.connection, () =>
      this.publicationResult(
        this.encryption.transaction(stores, mode, work, signal)
      )
    );
  }

  private async publicationResult<T>(result: Promise<T>): Promise<T> {
    try {
      return await result;
    } catch (error) {
      if (isQuotaExceededError(error))
        throw new PublicationError('LOCAL_QUOTA');
      throw error;
    }
  }

  private notifyPublication(records: readonly DocRecord[], origin?: string) {
    for (const record of records) {
      this.emit('update', record, origin);
      this.channel.postMessage({
        type: 'update',
        update: record,
        origin,
      } satisfies ChannelMessage);
    }
  }

  private async captureJournal(
    tx: PublicationTransaction<'readonly'>,
    context: IDBEncryptionContext,
    record: IDBPublicationRecord
  ) {
    for (const docId of [
      ...record.docIds,
      ...(record.encrypted?.companions ?? []).map(({ docId }) => docId),
    ])
      await context.capture(tx, docKeyScope(docId), false);
  }
  private async encodeJournal(
    context: IDBEncryptionContext,
    publication: LocalPublication,
    send: PendingPublication['send']
  ) {
    if (this.encryption.plain)
      return {
        publication: {
          ...publication,
          formatEpoch: PUBLICATION_JOURNAL_FORMAT,
        },
        send,
      };
    const anchor = publication.members[0].docId;
    const header = await context.seal(
      docKeyScope(anchor),
      localPayloadScope('journal', anchor, publication.operationId),
      'journal',
      encodeLocalJson({
        kind: publication.kind,
        formatEpoch: PUBLICATION_JOURNAL_FORMAT,
        send,
      })
    );
    const members = [];
    for (const { update, ...member } of publication.members)
      members.push({
        ...member,
        payload: await context.seal(
          docKeyScope(member.docId),
          localPayloadScope(
            'publication-member',
            member.docId,
            publication.operationId
          ),
          'publication-member',
          update
        ),
      });
    const companions = [];
    for (const { docId, update } of publication.companions)
      companions.push({
        docId,
        payload: await context.seal(
          docKeyScope(docId),
          localPayloadScope(
            'publication-member',
            docId,
            publication.operationId
          ),
          'publication-member',
          update
        ),
      });
    return {
      encrypted: { header, members, companions },
      storageRevision: crypto.randomUUID(),
      keyScopeId: [
        ...new Set(
          publicationGroupMembers(publication).map(member =>
            docKeyScope(member.docId)
          )
        ),
      ],
    };
  }
  private async pendingPublication(
    record: IDBPublicationRecord,
    context: IDBEncryptionContext
  ): Promise<PendingPublication> {
    if (
      ![
        'LOCAL_COMMITTED',
        'PENDING_REMOTE',
        'REBASING',
        'BLOCKED_CONFLICT',
      ].includes(record.state)
    )
      throw new PublicationError('LOCAL_CONFLICT');
    let publication: LocalPublication;
    let send: PendingPublication['send'];
    if (this.encryption.plain) {
      if (!record.publication) throw localAccountKeyError('CORRUPT');
      const { formatEpoch, ...stored } = record.publication;
      assertPublicationJournalFormat(formatEpoch);
      publication = {
        ...stored,
        companions: journalCompanions(formatEpoch, stored.companions),
      };
      send = record.send ?? null;
    } else {
      if (
        !record.encrypted ||
        record.publication !== undefined ||
        record.send !== undefined ||
        !record.storageRevision ||
        !record.encrypted.members.length ||
        record.docIds.length !== record.encrypted.members.length ||
        record.encrypted.members.some(
          (member, index) => member.docId !== record.docIds[index]
        )
      )
        throw localAccountKeyError('CORRUPT');
      const anchor = record.docIds[0];
      const value = decodeLocalJson(
        await context.open(
          docKeyScope(anchor),
          localPayloadScope('journal', anchor, record.operationId),
          'journal',
          record.encrypted.header
        )
      );
      if (
        !value ||
        typeof value !== 'object' ||
        !('kind' in value) ||
        !['create', 'update', 'delete', 'move', 'schema'].includes(
          String(value.kind)
        ) ||
        !('formatEpoch' in value) ||
        !('send' in value)
      )
        throw localAccountKeyError('CORRUPT');
      assertPublicationJournalFormat(value.formatEpoch);
      const header = value as {
        kind: LocalPublication['kind'];
        formatEpoch: string;
        send: PendingPublication['send'];
      };
      if (
        header.send &&
        (typeof header.send.profile !== 'string' ||
          !Number.isFinite(header.send.deadlineMs))
      )
        throw localAccountKeyError('CORRUPT');
      const members = await Promise.all(
        record.encrypted.members.map(async ({ payload, ...member }) => ({
          ...member,
          update: await context.open(
            docKeyScope(member.docId),
            localPayloadScope(
              'publication-member',
              member.docId,
              record.operationId
            ),
            'publication-member',
            payload
          ),
        }))
      );
      const companions = await Promise.all(
        journalCompanions(header.formatEpoch, record.encrypted.companions).map(
          async ({ docId, payload }) => ({
            docId,
            update: await context.open(
              docKeyScope(docId),
              localPayloadScope(
                'publication-member',
                docId,
                record.operationId
              ),
              'publication-member',
              payload
            ),
          })
        )
      );
      publication = {
        operationId: record.operationId,
        workspaceId: this.encryption.identity().workspaceId,
        kind: header.kind,
        members,
        companions,
      };
      send = header.send;
    }
    return {
      publication,
      sequence: record.sequence,
      mutationId: record.mutationId,
      state: record.state,
      attempt: record.attempt,
      send,
      refusal: record.refusal ?? null,
    };
  }

  async readPublicationBase(
    docIds: readonly string[],
    signal?: AbortSignal
  ): Promise<readonly PublicationMemberBase[]> {
    return this.readPublicationBaseWithCompaction(docIds, signal, true);
  }

  async readPublicationHeads(
    docIds: readonly string[],
    signal?: AbortSignal
  ): Promise<readonly PublicationMemberHead[]> {
    return this.readPublicationHeadsWithCompaction(docIds, signal, true);
  }

  /** Kept writer documents skip the base read, so the head read carries the
   * same bounded replay maintenance; compaction keeps clock and actor. */
  private async readPublicationHeadsWithCompaction(
    docIds: readonly string[],
    signal: AbortSignal | undefined,
    compact: boolean
  ): Promise<readonly PublicationMemberHead[]> {
    if (signal?.aborted) throw new PublicationError('CANCELLED');
    this.publicationBinding();
    const ids = [...docIds];
    if (new Set(ids).size !== ids.length)
      throw new PublicationError('FORMAT_MISMATCH');
    const { value, context } = await this.encryption.read(
      ['updates', 'clocks', 'publicationActors'],
      false,
      async tx => {
        const heads: { head: PublicationMemberHead; updates: number }[] = [];
        for (const docId of ids) {
          const updates = await tx
            .objectStore('updates')
            .index('docId')
            .count(docId);
          const clock = await tx.objectStore('clocks').get(docId);
          const actor = await tx.objectStore('publicationActors').get(docId);
          heads.push({
            head: {
              docId,
              clock: clock?.timestamp ?? null,
              actor: actor
                ? { clientId: actor.clientId, nextClock: actor.nextClock }
                : null,
            },
            updates,
          });
        }
        return heads;
      },
      signal
    );
    await this.encryption.assertRead(context);
    if (compact && !this.isReadonly) {
      const due = value.filter(
        entry => entry.updates >= PUBLICATION_BASE_COMPACTION_UPDATES
      );
      if (due.length) {
        for (const { head } of due) {
          if (signal?.aborted) throw new PublicationError('CANCELLED');
          await this.readDocWithCompaction(head.docId, true);
        }
        // One maintenance pass only, as for the base read.
        return this.readPublicationHeadsWithCompaction(ids, signal, false);
      }
    }
    return value.map(entry => entry.head);
  }

  private async readPublicationBaseWithCompaction(
    docIds: readonly string[],
    signal: AbortSignal | undefined,
    compact: boolean
  ): Promise<readonly PublicationMemberBase[]> {
    if (signal?.aborted) throw new PublicationError('CANCELLED');
    this.publicationBinding();
    const ids = [...docIds];
    if (new Set(ids).size !== ids.length)
      throw new PublicationError('FORMAT_MISMATCH');
    const { value, context } = await this.encryption.read(
      ['snapshots', 'updates', 'clocks', 'publicationActors'],
      false,
      async (tx, context) => {
        const bases = [];
        for (const docId of ids) {
          const snapshot = await tx.objectStore('snapshots').get(docId);
          const updates = await tx
            .objectStore('updates')
            .index('docId')
            .getAll(docId);
          const clock = await tx.objectStore('clocks').get(docId);
          const actor = await tx.objectStore('publicationActors').get(docId);
          if (snapshot || updates.length)
            await context.capture(tx, docKeyScope(docId), false);
          bases.push({ docId, snapshot, updates, clock, actor });
        }
        return bases;
      },
      signal
    );
    if (compact && !this.isReadonly) {
      const due = value.filter(
        base => base.updates.length >= PUBLICATION_BASE_COMPACTION_UPDATES
      );
      if (due.length) {
        await this.encryption.assertRead(context);
        for (const base of due) {
          if (signal?.aborted) throw new PublicationError('CANCELLED');
          // Reuse snapshot CAS and captured-update retirement. The separate
          // publication journal, clocks and actor ledger are never compacted.
          await this.readDocWithCompaction(base.docId, true);
        }
        // Capture bytes, clocks and actors together again after maintenance.
        // One maintenance pass only: competing writers cannot cause a retry loop.
        return this.readPublicationBaseWithCompaction(ids, signal, false);
      }
    }
    const result = await Promise.all(
      value.map(async base => {
        const bins = await Promise.all([
          ...(base.snapshot
            ? [
                this.readDocPayload(context, base.snapshot, 'snapshot').then(
                  payload => payload.bin
                ),
              ]
            : []),
          ...base.updates.map(update =>
            this.readDocPayload(context, update, 'update').then(
              payload => payload.bin
            )
          ),
        ]);
        return {
          docId: base.docId,
          bin: await this.mergePublicationParts(bins),
          clock: base.clock?.timestamp ?? null,
          actor: base.actor
            ? { clientId: base.actor.clientId, nextClock: base.actor.nextClock }
            : null,
        };
      })
    );
    await this.encryption.assertRead(context);
    return result;
  }

  private existingPublicationResult(
    record: IDBPublicationRecord,
    mutationId: string
  ): { receipt: LocalPublicationReceipt; records: DocRecord[] } {
    if (record.mutationId !== mutationId)
      throw new PublicationError('LOCAL_CONFLICT');
    return {
      receipt: {
        operationId: record.operationId,
        sequence: record.sequence,
        clocks: record.clocks,
        state: 'LOCAL_COMMITTED',
      },
      records: [],
    };
  }

  async commitPublication(
    input: LocalPublication,
    signal?: AbortSignal,
    measurement?: PublicationCommitMeasurement
  ): Promise<LocalPublicationReceipt> {
    const started = performance.now();
    if (measurement) measurement.adapter = 'idb';
    if (signal?.aborted) throw new PublicationError('CANCELLED');
    const binding = this.publicationBinding();
    const publication = structuredClone(input);
    if (publication.workspaceId !== (binding?.workspaceId ?? this.options.id)) {
      throw new PublicationError('FORBIDDEN');
    }
    if (!publication.members.length) {
      throw new PublicationError('FORMAT_MISMATCH');
    }
    validatePublicationUpdates(publication.members);
    validatePublicationCompanions(publication);
    const validated = performance.now();
    if (measurement) measurement.validationMs = validated - started;
    const mutationId = await computePublicationMutationId({
      kind: publication.kind,
      members: publicationGroupMembers(publication),
    });
    const identified = performance.now();
    if (measurement) measurement.identityMs = identified - validated;
    const result = await this.publicationResult(
      this.encryption.write(async () => {
        const entered = performance.now();
        if (measurement) {
          measurement.retryAndEntryMs = entered - identified;
          measurement.attempts = (measurement.attempts ?? 0) + 1;
        }
        const { value: prior, context } = await this.encryption.read(
          ['publications'],
          true,
          async (tx, context) => {
            const existing = await tx
              .objectStore('publications')
              .get(publication.operationId);
            if (existing) return existing;
            for (const member of publicationGroupMembers(publication))
              await context.capture(tx, docKeyScope(member.docId), true);
            return null;
          },
          signal
        );
        const authorityRead = performance.now();
        if (measurement) measurement.authorityReadMs = authorityRead - entered;
        if (prior) {
          await this.encryption.assertRead(context);
          return this.existingPublicationResult(prior, mutationId);
        }
        const payloads = await Promise.all(
          publication.members.map(member =>
            this.encodeDocPayload(
              context,
              { docId: member.docId, bin: member.update },
              'update'
            )
          )
        );
        const payloadEncoded = performance.now();
        if (measurement)
          measurement.payloadEncodeMs = payloadEncoded - authorityRead;
        const journalPayload = await this.encodeJournal(
          context,
          publication,
          null
        );
        const journalEncoded = performance.now();
        if (measurement)
          measurement.journalEncodeMs = journalEncoded - payloadEncoded;
        const committed = await this.encryption.commit(
          context,
          publicationStores,
          async tx => {
            const journal = tx.objectStore('publications');
            const records: DocRecord[] = [];
            for (const [index, member] of publication.members.entries()) {
              const previous = await tx.objectStore('clocks').get(member.docId);
              const actor = await tx
                .objectStore('publicationActors')
                .get(member.docId);
              if (
                (previous?.timestamp.getTime() ?? null) !==
                  (member.expectedClock?.getTime() ?? null) ||
                (actor?.clientId ?? null) !==
                  (member.expectedActor?.clientId ?? null) ||
                (actor?.nextClock ?? null) !==
                  (member.expectedActor?.nextClock ?? null) ||
                (actor && actor.clientId !== member.nextActor.clientId)
              ) {
                throw new PublicationError('LOCAL_CONFLICT');
              }
              const next = member.nextActor;
              const timestamp = new Date(
                Math.max(Date.now(), (previous?.timestamp.getTime() ?? -1) + 1)
              );
              await tx.objectStore('updates').add({
                docId: member.docId,
                ...payloads[index],
                createdAt: timestamp,
              });
              await tx
                .objectStore('clocks')
                .put({ docId: member.docId, timestamp });
              await tx
                .objectStore('publicationActors')
                .put({ docId: member.docId, ...next });
              records.push({
                docId: member.docId,
                bin: member.update,
                timestamp,
              });
            }
            const meta = tx.objectStore('publicationMeta');
            const sequence = ((await meta.get('sequence'))?.value ?? 0) + 1;
            if (!Number.isSafeInteger(sequence))
              throw new PublicationError('LOCAL_QUOTA');
            await meta.put({ key: 'sequence', value: sequence });
            const clocks = records.map(({ docId, timestamp }) => ({
              docId,
              timestamp,
            }));
            await journal.add({
              operationId: publication.operationId,
              mutationId,
              sequence,
              ...journalPayload,
              state: 'LOCAL_COMMITTED',
              attempt: 0,
              clocks,
              docIds: publication.members.map(member => member.docId),
            });
            return {
              receipt: {
                operationId: publication.operationId,
                sequence,
                clocks,
                state: 'LOCAL_COMMITTED' as const,
              },
              records,
            };
          },
          signal,
          async tx => {
            const existing = await tx
              .objectStore('publications')
              .get(publication.operationId);
            return existing
              ? { value: this.existingPublicationResult(existing, mutationId) }
              : null;
          }
        );
        if (measurement)
          measurement.transactionMs = performance.now() - journalEncoded;
        return committed;
      })
    );
    const notifyStarted = performance.now();
    this.advancePublicationTimestamp(result.receipt.clocks);
    this.notifyPublication(result.records);
    if (measurement) measurement.notifyMs = performance.now() - notifyStarted;
    return result.receipt;
  }

  async listPendingPublications(
    after: number,
    limit: number
  ): Promise<readonly PendingPublication[]> {
    this.publicationBinding();
    if (
      !Number.isSafeInteger(after) ||
      after < 0 ||
      !Number.isSafeInteger(limit) ||
      limit < 1
    ) {
      throw new PublicationError('FORMAT_MISMATCH');
    }
    const { value, context } = await this.encryption.read(
      ['publications'],
      false,
      async (tx, context) => {
        const result: IDBPublicationRecord[] = [];
        let cursor = await tx
          .objectStore('publications')
          .index('sequence')
          .openCursor(IDBKeyRange.lowerBound(after, true));
        while (cursor && result.length < limit) {
          const record = cursor.value;
          if (
            [
              'LOCAL_COMMITTED',
              'PENDING_REMOTE',
              'REBASING',
              'BLOCKED_CONFLICT',
            ].includes(record.state)
          ) {
            await this.captureJournal(tx, context, record);
            result.push(record);
          }
          if (result.length < limit) cursor = await cursor.continue();
        }
        return result;
      }
    );
    const result = await Promise.all(
      value.map(record => this.pendingPublication(record, context))
    );
    await this.encryption.assertRead(context);
    return result;
  }

  async beginPublicationAttempt(
    input: BeginPublicationAttemptInput
  ): Promise<PendingPublication> {
    this.publicationBinding();
    const request = structuredClone(input);
    const { value: record, context } = await this.encryption.read(
      ['publications'],
      false,
      async (tx, context) => {
        const record = await tx
          .objectStore('publications')
          .get(request.operationId);
        if (!record || record.state === 'BLOCKED_CONFLICT')
          throw new PublicationError('LOCAL_CONFLICT');
        await this.captureJournal(tx, context, record);
        return record;
      }
    );
    const pending = await this.pendingPublication(record, context);
    if (
      pending.send &&
      (pending.send.profile !== request.send.profile ||
        pending.send.deadlineMs !== request.send.deadlineMs)
    )
      throw new PublicationError('FORMAT_MISMATCH');
    const expected =
      record.state === 'LOCAL_COMMITTED'
        ? 1
        : record.state === 'REBASING'
          ? record.attempt + 1
          : null;
    if (expected !== request.attempt || !Number.isSafeInteger(request.attempt))
      throw new PublicationError('LOCAL_CONFLICT');
    // The immutable member containers survive policy/state transitions unchanged.
    let next: IDBPublicationRecord;
    if (this.encryption.plain)
      next = {
        ...record,
        attempt: request.attempt,
        send: request.send,
        state: 'PENDING_REMOTE',
      };
    else {
      const anchor = record.docIds[0];
      const publication = pending.publication;
      const header = await context.seal(
        docKeyScope(anchor),
        localPayloadScope('journal', anchor, record.operationId),
        'journal',
        encodeLocalJson({
          kind: publication.kind,
          formatEpoch: PUBLICATION_JOURNAL_FORMAT,
          send: request.send,
        })
      );
      next = {
        ...record,
        // The header now carries the current tag, whose entries name their
        // companions: an entry of the first tag has none.
        encrypted: {
          ...record.encrypted!,
          companions: record.encrypted!.companions ?? [],
          header,
        },
        storageRevision: crypto.randomUUID(),
        state: 'PENDING_REMOTE',
        attempt: request.attempt,
      };
    }
    await this.publicationResult(
      this.encryption.commit(context, ['publications'], async tx => {
        const current = await tx
          .objectStore('publications')
          .get(request.operationId);
        if (
          !current ||
          current.storageRevision !== record.storageRevision ||
          current.state !== record.state ||
          current.attempt !== record.attempt
        )
          throw new PublicationError('LOCAL_CONFLICT');
        await tx.objectStore('publications').put(next);
      })
    );
    return {
      ...pending,
      state: 'PENDING_REMOTE',
      attempt: request.attempt,
      send: request.send,
    };
  }

  async acknowledgePublication(
    input: AcknowledgePublicationInput
  ): Promise<void> {
    this.publicationBinding();
    const { peer, operationId, receipt: ack } = structuredClone(input);
    await this.publicationTransaction(
      'readwrite',
      [...publicationStores, 'peerClocks'],
      async tx => {
        const journal = tx.objectStore('publications');
        const record = await journal.get(operationId);
        if (
          !record ||
          record.state !== 'PENDING_REMOTE' ||
          !record.attempt ||
          publicationAttemptId(operationId, record.attempt) !== ack.operationId
        )
          return;
        const state = publicationStateFromReceipt(ack);
        if (state === 'REMOTE_COMMITTED') {
          for (const clock of record.clocks) {
            await putPeerClocksMax(tx, peer, clock.docId, {
              pushedClock: clock.timestamp,
            });
          }
          // Pushed clocks use the original local receipt, never a newer edit.
          // Terminal entries and their bytes leave the pending cursor atomically.
          await journal.delete(operationId);
        } else {
          const { refusal: _previous, ...entry } = record;
          const refusal = publicationRefusalFromReceipt(ack);
          await journal.put({
            ...entry,
            ...(state === 'BLOCKED_CONFLICT' && refusal ? { refusal } : {}),
            state,
            storageRevision: crypto.randomUUID(),
          });
        }
      }
    );
  }

  async applyRemotePublication(
    input: RemotePublicationApply,
    signal?: AbortSignal
  ): Promise<RemotePublicationLocalReceipt> {
    if (signal?.aborted) throw new PublicationError('CANCELLED');
    this.publicationBinding();
    const request = structuredClone(input);
    const members = request.views.flatMap(view =>
      view.members.map(member => ({ ...member, viewId: view.viewId }))
    );
    if (
      request.views.some(view => !view.members.length) ||
      !members.length ||
      new Set(members.map(member => member.docId)).size !== members.length ||
      members.some(
        member =>
          !Number.isFinite(member.timestamp) ||
          !Number.isFinite(new Date(member.timestamp).getTime())
      )
    ) {
      throw new PublicationError('FORMAT_MISMATCH');
    }
    validatePublicationUpdates(members);
    const records = await this.publicationResult(
      this.encryption.write(async () => {
        const { context } = this.encryption.plain
          ? { context: new IDBEncryptionContext(this.encryption, true) }
          : await this.encryption.read(
              [],
              true,
              async (tx, context) => {
                for (const member of members)
                  await context.capture(tx, docKeyScope(member.docId), true);
              },
              signal
            );
        const payloads = await Promise.all(
          members.map(member =>
            this.encodeDocPayload(
              context,
              { docId: member.docId, bin: member.update },
              'update'
            )
          )
        );
        return this.encryption.commit(
          context,
          [...publicationStores, 'peerClocks', 'remotePublicationMarks'],
          async tx => {
            const records: DocRecord[] = [];
            for (const [index, member] of members.entries()) {
              const previous = await tx.objectStore('clocks').get(member.docId);
              const timestamp = new Date(
                Math.max(Date.now(), (previous?.timestamp.getTime() ?? -1) + 1)
              );
              await tx.objectStore('updates').add({
                docId: member.docId,
                ...payloads[index],
                createdAt: timestamp,
              });
              await tx
                .objectStore('clocks')
                .put({ docId: member.docId, timestamp });
              await putPeerClocksMax(tx, request.peer, member.docId, {
                clock: new Date(member.timestamp),
                pulledClock: new Date(member.timestamp),
              });
              await tx.objectStore('remotePublicationMarks').put({
                peer: request.peer,
                docId: member.docId,
                viewId: member.viewId,
                snapshotId: member.snapshotId,
                lastSeq: member.lastSeq,
                contentHash: member.contentHash,
              });
              const agent = request.agents?.[member.docId];
              records.push({
                docId: member.docId,
                bin: member.update,
                timestamp,
                ...(agent ? { agent } : {}),
              });
            }
            return records;
          },
          signal
        );
      })
    );
    this.advancePublicationTimestamp(records);
    this.notifyPublication(records, docSyncRemoteApplyOrigin(request.peer));
    return {
      viewIds: request.views.map(view => view.viewId),
      clocks: records.map(({ docId, timestamp }) => ({ docId, timestamp })),
    };
  }

  private advancePublicationTimestamp(clocks: readonly DocClock[]) {
    for (const clock of clocks) {
      this.lastUpdateTimestampMs = Math.max(
        this.lastUpdateTimestampMs,
        clock.timestamp.getTime()
      );
    }
  }

  private nextUpdateTimestamp() {
    const now = Date.now();
    const next = Math.max(now, this.lastUpdateTimestampMs + 1);
    this.lastUpdateTimestampMs = next;
    return new Date(next);
  }

  override async pushDocUpdate(update: DocUpdate, origin?: string) {
    return withIdbRetry(this.connection, () =>
      this._pushDocUpdate(update, origin)
    );
  }

  private async _pushDocUpdate(input: DocUpdate, origin?: string) {
    const update = structuredClone(input);
    const result = await this.encryption.write(async () => {
      const { context } = this.encryption.plain
        ? { context: new IDBEncryptionContext(this.encryption) }
        : await this.encryption.read([], true, async (tx, context) => {
            await context.capture(tx, docKeyScope(update.docId), true);
          });
      const payload = await this.encodeDocPayload(context, update, 'update');
      return this.encryption.commit(
        context,
        ['updates', 'clocks'],
        async tx => {
          const previous = await tx.objectStore('clocks').get(update.docId);
          const timestamp = new Date(
            Math.max(
              this.nextUpdateTimestamp().getTime(),
              (previous?.timestamp.getTime() ?? -1) + 1
            )
          );
          await tx
            .objectStore('updates')
            .add({ ...payload, docId: update.docId, createdAt: timestamp });
          await tx
            .objectStore('clocks')
            .put({ docId: update.docId, timestamp });
          return { docId: update.docId, timestamp };
        }
      );
    });
    this.advancePublicationTimestamp([result]);
    this.notifyPublication(
      [
        {
          ...result,
          bin: update.bin,
          editor: update.editor,
          ...(update.agent ? { agent: update.agent } : {}),
          ...(update.clientAction ? { clientAction: update.clientAction } : {}),
        },
      ],
      origin
    );
    return result;
  }

  protected override async getDocSnapshot(docId: string) {
    return withIdbRetry(this.connection, async () => {
      const { value: record, context } = await this.encryption.read(
        ['snapshots'],
        false,
        async (tx, context) => {
          const record = await tx.objectStore('snapshots').get(docId);
          if (record) await context.capture(tx, docKeyScope(docId), false);
          return record;
        }
      );
      if (!record) return null;
      const result = await this.snapshotRecord(context, record);
      await this.encryption.assertRead(context);
      return result;
    });
  }

  /** Snapshot and updates in one transaction: one consistent view, one check. */
  protected override async getDocParts(docId: string) {
    return withIdbRetry(this.connection, async () => {
      const { value, context } = await this.encryption.read(
        ['snapshots', 'updates'],
        false,
        async (tx, context) => {
          const record = await tx.objectStore('snapshots').get(docId);
          const updates = await tx
            .objectStore('updates')
            .index('docId')
            .getAll(docId);
          if (record || updates.length)
            await context.capture(tx, docKeyScope(docId), false);
          return { record, updates };
        }
      );
      const [snapshot, updates] = await Promise.all([
        value.record ? this.snapshotRecord(context, value.record) : null,
        this.updateRecords(context, value.updates),
      ]);
      await this.encryption.assertRead(context);
      return { snapshot, updates };
    });
  }

  private async snapshotRecord(
    context: IDBEncryptionContext,
    record: DocStorageSchema['snapshots']['value']
  ) {
    const payload = await this.readDocPayload(context, record, 'snapshot');
    const result: DocRecord = {
      docId: record.docId,
      bin: payload.bin,
      editor: payload.editor,
      timestamp: record.updatedAt,
    };
    if (record.storageRevision)
      this.snapshotTokens.set(result, record.storageRevision);
    return result;
  }

  private updateRecords(
    context: IDBEncryptionContext,
    updates: DocStorageSchema['updates']['value'][]
  ) {
    return Promise.all(
      updates.map(async update => {
        const payload = await this.readDocPayload(context, update, 'update');
        const result: DocRecord = {
          docId: update.docId,
          bin: payload.bin,
          editor: payload.editor,
          timestamp: update.createdAt,
        };
        if (update.storageRevision)
          this.updateTokens.set(result, update.storageRevision);
        return result;
      })
    );
  }

  override async repairDoc(docId: string) {
    try {
      return await super.repairDoc(docId);
    } catch (error) {
      if (
        this.encryption.plain ||
        !error ||
        typeof error !== 'object' ||
        !('code' in error) ||
        error.code !== 'CORRUPT'
      )
        throw error;
    }
    await using _lock = await this.lockDocForUpdate(docId);
    const authority = await this.encryption.provider!.beginOperation();
    const removed = await this.encryption.transaction(
      ['snapshots', 'updates', 'docKeyRegistry'],
      'readwrite',
      async tx => {
        const registry = await tx
          .objectStore('docKeyRegistry')
          .index('keyScopeId')
          .get(docKeyScope(docId));
        if (!registry || registry.keyId !== authority.keyId)
          throw localAccountKeyError('KEY_UNAVAILABLE');
        const updates = tx.objectStore('updates');
        const keys = await updates.index('docId').getAllKeys(docId);
        for (const key of keys) await updates.delete(key);
        await tx.objectStore('snapshots').delete(docId);
        return keys.length;
      }
    );
    this.notifyLocalDocReplaced(docId);
    return { kept: 0, removed, snapshotRemoved: true, docReadable: false };
  }

  override async deleteDoc(docId: string) {
    return withIdbRetry(this.connection, () => this._deleteDoc(docId));
  }

  private async _deleteDoc(docId: string) {
    await this.encryption.transaction(
      ['snapshots', 'updates', 'clocks'],
      'readwrite',
      async trx => {
        const idx = trx.objectStore('updates').index('docId');
        const iter = idx.iterate(IDBKeyRange.only(docId));

        for await (const { value } of iter) {
          await trx
            .objectStore('updates')
            .delete([value.docId, value.createdAt]);
        }

        await trx.objectStore('snapshots').delete(docId);
        await trx.objectStore('clocks').delete(docId);
      }
    );
    this.notifyLocalDocReplaced(docId);
  }

  async replaceLocalDoc(record: DocRecord, origin?: string) {
    return withIdbRetry(this.connection, async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await this._replaceLocalDoc(record, origin);
        } catch (error) {
          if (
            !(error instanceof PublicationError) ||
            error.code !== 'LOCAL_CONFLICT' ||
            attempt === 2
          )
            throw error;
        }
      }
      throw new PublicationError('LOCAL_CONFLICT');
    });
  }

  private async _replaceLocalDoc(input: DocRecord, origin?: string) {
    const record = structuredClone(input);
    const basis = await this.encryption.transaction(
      ['snapshots', 'updates', 'clocks'],
      'readonly',
      async tx => ({
        snapshot: await tx.objectStore('snapshots').get(record.docId),
        updates: await tx
          .objectStore('updates')
          .index('docId')
          .getAll(record.docId),
        clock: await tx.objectStore('clocks').get(record.docId),
      })
    );
    await this.encryption.write(async () => {
      const { context } = this.encryption.plain
        ? { context: new IDBEncryptionContext(this.encryption, false) }
        : await this.encryption.read([], true, async (tx, context) => {
            await context.capture(tx, docKeyScope(record.docId), true);
          });
      const payload = await this.encodeDocPayload(context, record, 'snapshot');
      await this.encryption.commit(
        context,
        ['snapshots', 'updates', 'clocks'],
        async tx => {
          const previous = await tx.objectStore('snapshots').get(record.docId);
          const clock = await tx.objectStore('clocks').get(record.docId);
          if (
            (previous?.storageRevision ?? null) !==
              (basis.snapshot?.storageRevision ?? null) ||
            (clock?.timestamp.getTime() ?? null) !==
              (basis.clock?.timestamp.getTime() ?? null)
          )
            throw new PublicationError('LOCAL_CONFLICT');
          await tx.objectStore('snapshots').put({
            ...payload,
            docId: record.docId,
            createdAt: previous?.createdAt ?? record.timestamp,
            updatedAt: record.timestamp,
          });
          for (const update of basis.updates) {
            const current = await tx
              .objectStore('updates')
              .get([record.docId, update.createdAt]);
            if (current && current.storageRevision === update.storageRevision)
              await tx
                .objectStore('updates')
                .delete([record.docId, update.createdAt]);
          }
          await tx
            .objectStore('clocks')
            .put({ docId: record.docId, timestamp: record.timestamp });
        }
      );
    });
    this.notifyPublication([record], origin);
    this.notifyLocalDocReplaced(record.docId);
    return { docId: record.docId, timestamp: record.timestamp };
  }

  protected override notifyLocalDocReplaced(docId: string) {
    super.notifyLocalDocReplaced(docId);
    this.channel.postMessage({
      type: 'replaced',
      docId,
    } satisfies ChannelMessage);
  }

  protected override async deleteDocSnapshot(docId: string): Promise<void> {
    return withIdbRetry(this.connection, () =>
      this.encryption.transaction(['snapshots'], 'readwrite', async tx => {
        await tx.objectStore('snapshots').delete(docId);
      })
    );
  }
  override async getDocTimestamps(after: Date = new Date(0)) {
    return withIdbRetry(this.connection, () =>
      this.encryption.transaction(['clocks'], 'readonly', async tx => {
        const clocks = await tx.objectStore('clocks').getAll();
        return clocks.reduce((ret, cur) => {
          if (cur.timestamp > after) ret[cur.docId] = cur.timestamp;
          return ret;
        }, {} as DocClocks);
      })
    );
  }
  // The recovery version is no account data: it is read before any identity
  // is bound, so it bypasses the encryption context.
  async getRecoveryVersion(): Promise<number | null> {
    return withIdbRetry(this.connection, () =>
      readRecoveryVersion(this.connection.inner.db)
    );
  }

  async setRecoveryVersion(version: number): Promise<void> {
    await withIdbRetry(this.connection, async () => {
      const tx = this.connection.inner.db.transaction(
        'publicationMeta',
        'readwrite'
      );
      await tx.store.put({ key: 'recoveryVersion', value: version });
      await tx.done;
    });
  }

  override async getDocTimestamp(docId: string): Promise<DocClock | null> {
    return withIdbRetry(this.connection, () =>
      this.encryption.transaction(
        ['clocks'],
        'readonly',
        async tx => (await tx.objectStore('clocks').get(docId)) ?? null
      )
    );
  }
  protected override async setDocSnapshot(
    input: DocRecord,
    prevSnapshot: DocRecord | null
  ): Promise<boolean> {
    const snapshot = structuredClone(input);
    const previousToken = prevSnapshot
      ? this.snapshotTokens.get(prevSnapshot)
      : undefined;
    return withIdbRetry(this.connection, () =>
      this.encryption.write(async () => {
        const { value: basis, context } = await this.encryption.read(
          ['snapshots'],
          true,
          async (tx, context) => {
            await context.capture(tx, docKeyScope(snapshot.docId), true);
            return await tx.objectStore('snapshots').get(snapshot.docId);
          }
        );
        const payload = await this.encodeDocPayload(
          context,
          snapshot,
          'snapshot'
        );
        return this.encryption.commit(context, ['snapshots'], async tx => {
          const current = await tx.objectStore('snapshots').get(snapshot.docId);
          if (
            (current?.storageRevision ?? null) !==
            (basis?.storageRevision ?? null)
          )
            throw new SnapshotCASMiss();
          const sameBasis =
            current &&
            prevSnapshot &&
            current.updatedAt.getTime() === snapshot.timestamp.getTime() &&
            current.updatedAt.getTime() === prevSnapshot.timestamp.getTime() &&
            (this.encryption.plain
              ? current.bin?.byteLength === prevSnapshot.bin.byteLength &&
                current.bin.every(
                  (byte, index) => byte === prevSnapshot.bin[index]
                )
              : !!previousToken && current.storageRevision === previousToken);
          if (
            current &&
            !(current.updatedAt < snapshot.timestamp) &&
            !sameBasis
          )
            throw new SnapshotCASMiss();
          await tx.objectStore('snapshots').put({
            ...payload,
            docId: snapshot.docId,
            createdAt: current?.createdAt ?? snapshot.timestamp,
            updatedAt: snapshot.timestamp,
          });
          return true;
        });
      })
    ).catch(error => {
      if (error instanceof SnapshotCASMiss) return false;
      throw error;
    });
  }
  protected override async getDocUpdates(docId: string): Promise<DocRecord[]> {
    return withIdbRetry(this.connection, async () => {
      const { value: updates, context } = await this.encryption.read(
        ['updates'],
        false,
        async (tx, context) => {
          const updates = await tx
            .objectStore('updates')
            .index('docId')
            .getAll(docId);
          if (updates.length)
            await context.capture(tx, docKeyScope(docId), false);
          return updates;
        }
      );
      const results = await this.updateRecords(context, updates);
      await this.encryption.assertRead(context);
      return results;
    });
  }
  protected override async markUpdatesMerged(
    docId: string,
    updates: DocRecord[]
  ): Promise<number> {
    return withIdbRetry(this.connection, () =>
      this.encryption.transaction(['updates'], 'readwrite', async tx => {
        let deleted = 0;
        for (const update of updates) {
          const current = await tx
            .objectStore('updates')
            .get([docId, update.timestamp]);
          if (!current) continue;
          if (
            !this.encryption.plain &&
            (!this.updateTokens.has(update) ||
              current.storageRevision !== this.updateTokens.get(update))
          )
            continue;
          await tx.objectStore('updates').delete([docId, update.timestamp]);
          deleted++;
        }
        return deleted;
      })
    );
  }

  private channelListeners = 0;
  private stopChannel: (() => void) | undefined;

  /**
   * Listens to other tabs of this store while any subscriber needs them, on
   * the channel of every connection, so a subscription made before connecting
   * or kept across a reconnect hears them too.
   */
  private listenToChannel() {
    if (this.channelListeners++ === 0) {
      const attach = () =>
        this.connection.maybeConnection?.channel.addEventListener(
          'message',
          this.handleChannelMessage
        );
      attach();
      const off = this.connection.onStatusChanged(status => {
        if (status === 'connected') attach();
      });
      this.stopChannel = () => {
        off();
        this.connection.maybeConnection?.channel.removeEventListener(
          'message',
          this.handleChannelMessage
        );
      };
    }
    return () => {
      if (--this.channelListeners === 0) {
        this.stopChannel?.();
        this.stopChannel = undefined;
      }
    };
  }

  override subscribeDocUpdate(
    callback: (update: DocRecord, origin?: string) => void
  ): () => void {
    const stop = this.listenToChannel();
    const dispose = super.subscribeDocUpdate(callback);
    return () => {
      dispose();
      stop();
    };
  }

  override subscribeLocalDocReplaced(
    callback: (docId: string) => void
  ): () => void {
    const stop = this.listenToChannel();
    const dispose = super.subscribeLocalDocReplaced(callback);
    return () => {
      dispose();
      stop();
    };
  }

  handleChannelMessage = (event: MessageEvent<ChannelMessage>) => {
    // A locked connection passes no plain update of another one on.
    if (!this.encryption.authorized) return;
    if (event.data.type === 'update') {
      this.emit('update', event.data.update, event.data.origin);
    } else if (event.data.type === 'replaced') {
      this.emit('localDocReplaced', event.data.docId);
    }
  };
}
